// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * ProductRow component for displaying a single product's asset counts across locations.
 */
import { Link } from '@/components/link';
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { VisibilityOffOutlined } from '@/constants/hardfin-standard-icons';
import { AssetCell } from '@/inventory/components/overview/components/asset-cell';
import { INVENTORY_OVERVIEW_COLORS } from '@/inventory/components/overview/constants';
import {
  cellStyles,
  gridRowStyles,
  locationCellStyles,
  totalCellStyles,
  typographyStyles,
} from '@/inventory/components/overview/styles';
import {
  OverviewLocation,
  OverviewProductRow,
} from '@/inventory/components/overview/types';
import { navigateToAssets } from '@/inventory/components/overview/utils/asset-navigation';
import { calculateLocationCounts } from '@/inventory/components/overview/utils/transform-inventory-data';
import { Box, Tooltip } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';

interface ProductRowProps {
  productRow: OverviewProductRow;
  inventoryLocations: OverviewLocation[];
  nonInventoryLocations: OverviewLocation[];
  isInventoryExpanded: boolean;
  isNonInventoryExpanded: boolean;
  isInTransitExpanded: boolean;
  gridTemplate: string;
  onAssetClick?: (
    productKey: string,
    locationKey: string,
    status: AssetFunctionalStatus
  ) => void;
  showScrappedAssets?: boolean;
  hideZeroes?: boolean;
}

/**
 * ProductRow component
 */
export function ProductRow(props: ProductRowProps) {
  const navigate = useNavigate();

  // Build display text with SKU first, name in parentheses
  const productSku = props.productRow.product.sku;
  const displayText = productSku
    ? `${productSku} (${props.productRow.product.name})`
    : props.productRow.product.name;

  // Calculate truncation based on current column dimensions
  // Products column: 225px, padding: 32px (16px each side), gap: 8px, available: ~185px
  // Font: 0.875rem (~7.5px per character), so ~24 characters fit
  const isTextTruncated = displayText.length > 24;

  const handleAssetClick = (
    locationId: string,
    status: AssetFunctionalStatus
  ) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      locationIds: [locationId],
      functionalStatus: status,
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });

    // Still call the original callback if provided
    if (props.onAssetClick) {
      props.onAssetClick(props.productRow.product.key, locationId, status);
    }
  };

  const handleAssetTotalClick = (locationId: string) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      locationIds: [locationId],
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInventoryTotalClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      inventoryStatus: ['in_inventory'],
      functionalStatus: status,
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInventoryTotalNumberClick = () => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      inventoryStatus: ['in_inventory'],
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleNonInventoryTotalClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      inventoryStatus: ['not_in_inventory'],
      functionalStatus: status,
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleNonInventoryTotalNumberClick = () => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      inventoryStatus: ['not_in_inventory'],
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInTransitClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      transitStatus: ['in_transit_to_inventory', 'in_transit_to_field'],
      functionalStatus: status,
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInTransitTotalNumberClick = () => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      transitStatus: ['in_transit_to_inventory', 'in_transit_to_field'],
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInTransitToInventoryClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      transitStatus: ['in_transit_to_inventory'],
      functionalStatus: status,
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInTransitToInventoryTotalClick = () => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      transitStatus: ['in_transit_to_inventory'],
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInTransitToNonInventoryClick = (
    status: AssetFunctionalStatus
  ) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      transitStatus: ['in_transit_to_field'],
      functionalStatus: status,
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleInTransitToNonInventoryTotalClick = () => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      transitStatus: ['in_transit_to_field'],
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleGrandTotalClick = (status: AssetFunctionalStatus) => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      functionalStatus: status,
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  const handleGrandTotalNumberClick = () => {
    navigateToAssets({
      productIds: [props.productRow.product.id],
      showScrappedAssets: props.showScrappedAssets ?? false,
      navigate,
    });
  };

  return (
    <Box
      sx={{
        ...gridRowStyles.productRow,
        gridTemplateColumns: props.gridTemplate,
      }}
    >
      {/* Product name */}
      <Box sx={cellStyles.productName}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Tooltip title={isTextTruncated ? displayText : ''} arrow>
            <Link
              to="/catalog/items/$itemKey/assets"
              params={{ itemKey: props.productRow.product.key }}
              search={
                (props.showScrappedAssets ?? false)
                  ? { scrapped: true }
                  : undefined
              }
              sx={{
                ...typographyStyles.productLink,
                color: props.productRow.product.isArchived
                  ? 'text.disabled'
                  : 'text.primary',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayText}
            </Link>
          </Tooltip>
          {props.productRow.product.isArchived && (
            <VisibilityOffOutlined fontSize="small" color="disabled" />
          )}
        </Box>
      </Box>

      {/* Inventory locations */}
      {props.isInventoryExpanded &&
        props.inventoryLocations.map((location) => (
          <Box key={location.location.key} sx={locationCellStyles.inventory}>
            <AssetCell
              counts={
                props.productRow.locationCounts[location.location.key] || {
                  FUNCTIONAL: 0,
                  NEEDS_REVIEW: 0,
                  NON_FUNCTIONAL: 0,
                  SCRAPPED: 0,
                }
              }
              onStatusClick={(status) =>
                handleAssetClick(location.location.id, status)
              }
              onTotalClick={() => handleAssetTotalClick(location.location.id)}
              showScrappedAssets={props.showScrappedAssets ?? false}
              hideZeroes={props.hideZeroes ?? false}
            />
          </Box>
        ))}

      {/* Inventory total - always show */}
      <Box sx={totalCellStyles.inventory}>
        <AssetCell
          counts={calculateLocationCounts(
            props.inventoryLocations,
            props.productRow.locationCounts
          )}
          onStatusClick={handleInventoryTotalClick}
          onTotalClick={handleInventoryTotalNumberClick}
          isTotal
          showScrappedAssets={props.showScrappedAssets ?? false}
          hideZeroes={props.hideZeroes ?? false}
        />
      </Box>

      {/* Non-inventory locations */}
      {props.isNonInventoryExpanded &&
        props.nonInventoryLocations.map((location) => (
          <Box key={location.location.key} sx={locationCellStyles.nonInventory}>
            <AssetCell
              counts={
                props.productRow.locationCounts[location.location.key] || {
                  FUNCTIONAL: 0,
                  NEEDS_REVIEW: 0,
                  NON_FUNCTIONAL: 0,
                  SCRAPPED: 0,
                }
              }
              onStatusClick={(status) =>
                handleAssetClick(location.location.id, status)
              }
              onTotalClick={() => handleAssetTotalClick(location.location.id)}
              showScrappedAssets={props.showScrappedAssets ?? false}
              hideZeroes={props.hideZeroes ?? false}
            />
          </Box>
        ))}

      {/* Non-inventory total - always show */}
      <Box sx={totalCellStyles.nonInventory}>
        <AssetCell
          counts={calculateLocationCounts(
            props.nonInventoryLocations,
            props.productRow.locationCounts
          )}
          onStatusClick={handleNonInventoryTotalClick}
          onTotalClick={handleNonInventoryTotalNumberClick}
          isTotal
          showScrappedAssets={props.showScrappedAssets ?? false}
          hideZeroes={props.hideZeroes ?? false}
        />
      </Box>

      {/* Individual in-transit columns */}
      {props.isInTransitExpanded && (
        <>
          {/* In transit to inventory */}
          <Box
            sx={{
              ...totalCellStyles.inTransit,
              backgroundColor:
                INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.LOCATION_BACKGROUND,
            }}
          >
            <AssetCell
              counts={props.productRow.inTransitToInventoryCounts}
              onStatusClick={handleInTransitToInventoryClick}
              onTotalClick={handleInTransitToInventoryTotalClick}
              showScrappedAssets={props.showScrappedAssets ?? false}
              hideZeroes={props.hideZeroes ?? false}
            />
          </Box>

          {/* In transit to the field */}
          <Box
            sx={{
              ...totalCellStyles.inTransit,
              backgroundColor:
                INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.LOCATION_BACKGROUND,
            }}
          >
            <AssetCell
              counts={props.productRow.inTransitToNonInventoryCounts}
              onStatusClick={handleInTransitToNonInventoryClick}
              onTotalClick={handleInTransitToNonInventoryTotalClick}
              showScrappedAssets={props.showScrappedAssets ?? false}
              hideZeroes={props.hideZeroes ?? false}
            />
          </Box>
        </>
      )}

      {/* In transit total */}
      <Box sx={totalCellStyles.inTransit}>
        <AssetCell
          counts={props.productRow.inTransitCounts}
          onStatusClick={handleInTransitClick}
          onTotalClick={handleInTransitTotalNumberClick}
          isTotal
          showScrappedAssets={props.showScrappedAssets ?? false}
          hideZeroes={props.hideZeroes ?? false}
        />
      </Box>

      {/* Empty flexible space */}
      <Box sx={{ backgroundColor: 'grey.50' }} />

      {/* Total */}
      <Box sx={totalCellStyles.grand}>
        <AssetCell
          counts={props.productRow.totalCounts}
          onStatusClick={handleGrandTotalClick}
          onTotalClick={handleGrandTotalNumberClick}
          isTotal
          showScrappedAssets={props.showScrappedAssets ?? false}
          hideZeroes={props.hideZeroes ?? false}
        />
      </Box>
    </Box>
  );
}
